// /requisitions: your own requests, plus the approver's and the buyer's queues
// as tabs for the people on those lists. The server decides who sees which tab
// (GET /requisitions/me); a tab you cannot act on is not drawn.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import PageHeader, { PrimaryAction } from '../components/PageHeader';
import PageLayout from '../components/ui/PageLayout';
import { FilterBar, FilterSearch } from '../components/FilterBar';
import SegTrack from '../components/ui/SegTrack';
import { DataTable, type DataTableHandle } from '../components/table/DataTable';
import { useToast } from '../components/Toast/ToastContext';
import { formatEventDate, formatTimestamp } from '../utils/date-utils';
import StatusChip, { UrgentTag } from './StatusChip';
import { buttonClass } from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import Appear from '../components/ui/Appear';
import {
  STATUS_LABELS,
  centsToDollars,
  errorMessage,
  formatCents,
  useRequisitionsApi,
  type ListScope,
  type MeResponse,
  type RequisitionSummary,
} from './requisitionsApi';

interface Tab {
  key: ListScope;
  label: string;
  /** Who gets the tab, read off GET /me. */
  shown: (me: MeResponse | null) => boolean;
  count?: keyof MeResponse['counts'];
}

const TABS: Tab[] = [
  { key: 'mine', label: 'Mine', shown: () => true },
  { key: 'pending', label: 'Waiting on me', shown: (me) => !!me?.is_approver, count: 'pending' },
  { key: 'to_buy', label: 'To buy', shown: (me) => !!me?.is_orderer, count: 'to_buy' },
  { key: 'receipt_missing', label: 'Receipt missing', shown: (me) => !!me?.is_orderer, count: 'receipt_missing' },
  { key: 'on_the_way', label: 'On the way', shown: (me) => !!me?.is_orderer, count: 'on_the_way' },
  { key: 'all', label: 'All', shown: (me) => !!(me?.is_approver || me?.is_orderer) },
];

const TAB_HINTS: Record<ListScope, string> = {
  mine: 'Requests you asked for',
  pending: 'Requests waiting for your approval',
  to_buy: 'Approved requests nobody has bought yet',
  receipt_missing: 'Bought, with the receipt still to come',
  on_the_way: 'Bought and not arrived yet',
  all: 'Every request, newest first',
};

const EMPTY_COPY: Record<ListScope, { title: string; body: string }> = {
  mine: {
    title: 'You have not asked for anything yet',
    body: 'Need a shelf, a tool or desk supplies? Start a request and an approver will see it.',
  },
  pending: {
    title: 'Nothing is waiting on you',
    body: 'New requests show up here, and in Slack, when someone sends one.',
  },
  to_buy: {
    title: 'Nothing is ready to buy',
    body: 'Approved requests land here until someone marks them bought.',
  },
  receipt_missing: {
    title: 'No receipts outstanding',
    body: 'Requests bought with the receipt to follow wait here until it is linked.',
  },
  on_the_way: {
    title: 'Nothing is on the way',
    body: 'Bought requests wait here until someone marks them arrived.',
  },
  all: {
    title: 'No requests yet',
    body: 'Once someone sends a request, it shows up here.',
  },
};

const getRowId = (r: RequisitionSummary) => r.id;

// Module scope on purpose (the DataTable contract): every cell is pure over the row.
const COLUMNS: ColumnDef<RequisitionSummary, any>[] = [
  {
    id: 'req_number',
    header: 'Request',
    meta: { cellClass: 'whitespace-nowrap', csv: (r) => r.req_number },
    cell: ({ row }) => (
      <Link
        to={`/requisitions/${row.original.id}`}
        className="font-mono text-sm text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded"
      >
        {row.original.req_number}
      </Link>
    ),
  },
  {
    id: 'purpose',
    header: 'What for',
    meta: {
      cellClass: 'max-w-[22rem] truncate text-on-surface',
      csv: (r) => r.purpose,
      cellAttrs: (r) => ({ title: r.purpose }),
    },
    cell: ({ row }) => row.original.purpose,
  },
  {
    id: 'requester',
    header: 'Asked by',
    meta: { cellClass: 'whitespace-nowrap text-on-surface-variant', csv: (r) => r.requester?.name || '' },
    cell: ({ row }) => row.original.requester?.name || '—',
  },
  {
    id: 'status',
    header: 'Status',
    meta: { csv: (r) => STATUS_LABELS[r.status] },
    cell: ({ row }) => (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
        <StatusChip status={row.original.status} />
        <Appear show={row.original.status === 'bought' && !!row.original.receipt_to_follow} className="text-[11px] text-on-surface-variant">receipt to follow</Appear>
      </span>
    ),
  },
  {
    id: 'needed_by',
    header: 'Needed by',
    meta: {
      numeric: true,
      cellClass: 'whitespace-nowrap text-on-surface-variant',
      csv: (r) => r.needed_by || '',
    },
    cell: ({ row }) => (
      <span className="inline-flex items-center gap-1.5">
        {row.original.needed_by ? formatEventDate(row.original.needed_by, 'MMM D') : 'Any time'}
        <UrgentTag urgency={row.original.urgency} />
      </span>
    ),
  },
  {
    id: 'items',
    header: 'Items',
    meta: { align: 'right', numeric: true, cellClass: 'text-on-surface-variant', csv: (r) => r.line_count },
    cell: ({ row }) => row.original.line_count,
  },
  {
    id: 'estimate',
    header: 'About',
    meta: {
      align: 'right',
      numeric: true,
      cellClass: 'whitespace-nowrap text-on-surface',
      csv: (r) => centsToDollars(r.estimated_total_cents),
    },
    cell: ({ row }) => formatCents(row.original.estimated_total_cents),
  },
  {
    id: 'updated',
    header: 'Last change',
    meta: {
      numeric: true,
      cellClass: 'whitespace-nowrap text-on-surface-variant',
      csv: (r) => r.updated_at,
    },
    cell: ({ row }) => formatTimestamp(row.original.updated_at, 'MMM D'),
  },
];

const SKELETON_ROWS = [0, 1, 2, 3];

const RequisitionsPage: React.FC = () => {
  const reqApi = useRequisitionsApi();
  const toast = useToast();
  const history = useHistory();
  const location = useLocation();

  const [me, setMe] = useState<MeResponse | null>(null);
  const [meLoaded, setMeLoaded] = useState(false);
  // Starts on the tab a link asked for, so arriving from the bell fetches that
  // queue once instead of Mine first. Settled against the roles below.
  const [activeTab, setActiveTab] = useState<ListScope>(() => {
    const t = new URLSearchParams(location.search).get('tab');
    return TABS.some((x) => x.key === t) ? (t as ListScope) : 'mine';
  });
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<RequisitionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const tableRef = useRef<DataTableHandle>(null);

  // A link may arrive asking for a tab: the bell sends approvers to ?tab=pending
  // and buyers to ?tab=to_buy. The param is an entrance, not state.
  const requestedTab = useMemo(() => {
    const t = new URLSearchParams(location.search).get('tab');
    return TABS.some((x) => x.key === t) ? (t as ListScope) : null;
  }, [location.search]);

  const loadMe = useCallback(async () => {
    try {
      setMe(await reqApi.me());
    } catch {
      // The tabs are a convenience over Mine, which works without this.
    } finally {
      setMeLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const visibleTabs = TABS.filter((t) => t.shown(me));

  // Settle the tab once the roles are known: honor the link if the person has
  // that tab, otherwise land on Mine.
  useEffect(() => {
    if (!meLoaded) return;
    if (requestedTab && TABS.find((t) => t.key === requestedTab)?.shown(me)) setActiveTab(requestedTab);
    else if (!TABS.find((t) => t.key === activeTab)?.shown(me)) setActiveTab('mine');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meLoaded, requestedTab]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setRows(await reqApi.list(activeTab, search));
    } catch (err) {
      setRows([]);
      setLoadError(errorMessage(err, 'Could not load requests.'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, search]);

  // A queue tab waits for the roles, so a link to ?tab=pending never fires a
  // request the server would refuse.
  const ready = activeTab === 'mine' || meLoaded;
  const firstFetch = useRef(true);
  useEffect(() => {
    if (!ready) return;
    const t = setTimeout(() => {
      load();
      // Counts move with the rows, so a tab badge never disagrees with the
      // table. The first fetch skips it: the mount already asked.
      if (firstFetch.current) firstFetch.current = false;
      else loadMe();
    }, search ? 250 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, ready]);

  const n = rows.length;
  const tools = [
    {
      label: 'Export CSV',
      hint: `${n} row${n === 1 ? '' : 's'}, current tab and search`,
      disabled: loading || n === 0,
      onSelect: () => {
        const count = tableRef.current?.exportCsv('requests');
        if (count == null) {
          toast.error('The list is still loading. Try again in a moment.');
          return;
        }
        toast.success(`Exported ${count} row${count === 1 ? '' : 's'}.`);
      },
    },
  ];

  const empty = loadError ? (
    <EmptyState
      variant="area"
      title={loadError}
      action={
        <button type="button" onClick={() => load()} className={buttonClass('tonal')}>
          Try again
        </button>
      }
    >
      Check your connection, then try again.
    </EmptyState>
  ) : search.trim() ? (
    <EmptyState
      variant="area"
      title={<>No requests match “{search.trim()}”</>}
      action={
        <button type="button" onClick={() => setSearch('')} className={buttonClass('quiet')}>
          Clear search
        </button>
      }
    >
      Search looks at the request number, what it is for, the items and who asked.
    </EmptyState>
  ) : (
    <EmptyState
      variant="area"
      title={EMPTY_COPY[activeTab].title}
      action={
        activeTab === 'mine' ? (
          <Link to="/requisitions/new" className={buttonClass('filled')}>
            New request
          </Link>
        ) : undefined
      }
    >
      {EMPTY_COPY[activeTab].body}
    </EmptyState>
  );

  return (
    <PageLayout width="wide" kind="workspace" className="pt-4 space-y-5">
      <PageHeader
        title="Requests"
        helpTitle="About requests"
        lede="Ask for something to be bought, and follow it until it arrives."
        primary={
          <PrimaryAction to="/requisitions/new">
            <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" className="h-3.5 w-3.5">
              <path fill="currentColor" d="M8 2.5a.75.75 0 0 1 .75.75v4h4a.75.75 0 0 1 0 1.5h-4v4a.75.75 0 0 1-1.5 0v-4h-4a.75.75 0 0 1 0-1.5h4v-4A.75.75 0 0 1 8 2.5Z" />
            </svg>
            New request
          </PrimaryAction>
        }
        tools={tools}
        help={
          <>
            <p>
              A request asks for something to be bought for work. You say what you need, an
              approver says yes or no, and a buyer orders it on a company card. You hear about
              each step in Slack and by email.
            </p>
            <p className="font-medium text-on-surface">The tabs</p>
            <p>
              <span className="text-on-surface">Mine</span> is everything you asked for.
              {me?.is_approver && (
                <>
                  {' '}
                  <span className="text-on-surface">Waiting on me</span> holds requests you can
                  approve or deny.
                </>
              )}
              {me?.is_orderer && (
                <>
                  {' '}
                  <span className="text-on-surface">To buy</span> is approved and not bought
                  yet. <span className="text-on-surface">Receipt missing</span> is bought with
                  the receipt still to come. <span className="text-on-surface">On the way</span>{' '}
                  is bought and not arrived.
                </>
              )}
              {(me?.is_approver || me?.is_orderer) && (
                <>
                  {' '}
                  <span className="text-on-surface">All</span> is every request.
                </>
              )}
            </p>
            <p className="font-medium text-on-surface">The steps</p>
            <p>
              Draft, then Waiting for approval, then Ready to buy, then Bought, then Arrived. A
              denied request goes back to its draft with the reason. A buyer can also say Won&apos;t
              buy, with a reason, and the person who asked can cancel until it is bought.
            </p>
            <p className="font-medium text-on-surface">Moving around</p>
            <p>
              J and K move through the list, and Enter opens the highlighted request.
            </p>
          </>
        }
      />

      <FilterBar>
        {visibleTabs.length > 1 && (
          <SegTrack
            label="Which requests to show"
            options={visibleTabs.map((t) => ({
              key: t.key,
              label: t.label,
              badge: t.count && me ? me.counts[t.count] : undefined,
              hint: TAB_HINTS[t.key],
            }))}
            value={activeTab}
            onChange={(k) => { setRows([]); setActiveTab(k as ListScope); }}
            size="sm"
            wrap
          />
        )}
        <FilterSearch
          value={search}
          onChange={setSearch}
          label="Search requests by number, purpose, item or person"
          placeholder="Search requests…"
        />
      </FilterBar>

      <DataTable<RequisitionSummary>
        ref={tableRef}
        data={rows}
        columns={COLUMNS}
        getRowId={getRowId}
        stickyHeader
        // On Mine every row is yours; the column would only repeat your name.
        columnVisibility={activeTab === 'mine' ? { requester: false } : undefined}
        rowKeyboard={{ onOpen: (r) => history.push(`/requisitions/${r.id}`) }}
        rowProps={(r) => ({
          className: 'cursor-pointer',
          onClick: (e: React.MouseEvent) => {
            if ((e.target as HTMLElement).closest('a,button')) return;
            history.push(`/requisitions/${r.id}`);
          },
        })}
        strip={
          <span className="ml-auto text-xs tabular-nums text-on-surface-variant" role="status" aria-live="polite">
            {loading ? 'Loading…' : `${n} request${n === 1 ? '' : 's'}`}
          </span>
        }
        loading={loading}
        loadingContent={
          <SkeletonGroup label="Loading requests" className="space-y-2 px-4 py-4">
            {SKELETON_ROWS.map((i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-4 w-24 rounded-full" />
                <Skeleton className="hidden h-4 w-16 sm:block" />
              </div>
            ))}
          </SkeletonGroup>
        }
        empty={empty}
      />
    </PageLayout>
  );
};

export default RequisitionsPage;
